<script lang="ts">
  /* A label above a number.
   *
   * ═══ THE MOST REPEATED SHAPE IN THE INTERFACE, AND IT HAD NO NAME ═══
   *
   * Written as a `<dl>` in three files — RouteContent's `.sum`, SchedulePanel's
   * `.tot`, and the object sheet — with three different grids and three
   * different label sizes.
   *
   * ═══ AND `lg` IS THE ONE NEW THING ON SCREEN ═══
   *
   * The hosted chart has no large numerals anywhere. "How long is this passage"
   * renders as 13 px mono inside a peek line, below a heading, beside a hint —
   * so nothing in the interface reads as a result. The boat has always had
   * `.bignum` for exactly this. A trip planner that will not say its answer out
   * loud is not competing with anything.
   *
   * Use `lg` for the one or two numbers a panel exists to produce and the plain
   * size for everything supporting them. Three large numbers in a row is none.
   */
  let {
    label,
    /** Already formatted. This component does no arithmetic and no rounding —
     *  the callers own their units, and a value that is not known is `—`
     *  rather than a zero. This app never simulates data to fill a slot. */
    value,
    /** Set beside the value at body size, so a column of figures still lines
     *  up on the digits. */
    unit,
    size = 'md',
    /** Amber. For a number that is a WAIT — never folded into a duration,
     *  because a route that is an hour slower because it holds for slack is a
     *  different plan from one that is an hour slower because the tide is
     *  foul, and the skipper decides which they wanted. */
    tone = 'normal'
  }: {
    label: string;
    value: string;
    unit?: string;
    size?: 'md' | 'lg';
    tone?: 'normal' | 'warn' | 'bad';
  } = $props();
</script>

<div class="stat {size} {tone}">
  <span class="lb">{label}</span>
  <span class="v">{value}{#if unit}<span class="u">{unit}</span>{/if}</span>
</div>

<style>
  .stat {
    display: flex;
    flex-direction: column;
    gap: var(--sp-1);
    min-width: 0;
  }
  .lb {
    font: var(--fs-label) / 1 var(--px);
    letter-spacing: var(--tr-label);
    color: var(--sub);
    text-transform: uppercase;
  }
  .v {
    font: var(--fs-num) / var(--lh-tight) var(--mono);
    color: var(--ink);
    /* Tabular, so a column of times lines up and the eye can run down it —
       which is the whole reason a schedule is readable. */
    font-variant-numeric: tabular-nums;
  }
  .lg .v {
    font-size: var(--fs-num-lg);
    letter-spacing: -0.01em;
  }
  .u {
    font-size: var(--fs-body);
    color: var(--sub);
    margin-left: 1px;
  }
  .warn .v {
    color: var(--am);
  }
  .bad .v {
    color: var(--red);
  }
</style>
